import { SLUGS } from '@motorway/mw-enquiry-states/static';

import type { PhotoStatusType, VehicleDetails } from 'Utilities/profiling/@types';

export const TRADE_PATHNAME = '/trade';

export const PROFILING_PATH = {
  ACCOUNT: '/account',
  CONFIRM: '/confirm-details',
  CONTACTS: '/details',
  PHOTOS: '/photos',
  PROFILE: '/profile',
  PROOF_OF_PURCHASE: '/ownership/proof-of-purchase',
  SERVICE: '/service',
  SERVICE_UPLOAD: '/service/upload',
  START: '/features-and-equipment/vehicle-specs',
  SUMMARY: '/summary',
  TIMELINE: '/timeline',
  UPDATED_VALUATION: '/valuation',
  VEHICLE_PROFILE: '/vehicle-profile',
};

export const HYBRID_FUEL_MAPPING = Object.freeze([
  'electric diesel',
  'hybrid',
  'hybrid electric',
  'hybrid electric (clean)',
  'petrol/ele',
  'hyb-diesel',
  'hyb-petrol',
  'p/electric',
  'petrol/electric',
  'd/electric',
  'p/elecplug',
]);

export const ELECTRIC_FUEL_MAPPING = Object.freeze([
  'electric',
  'electricity',
]);

export const SWITCH_TIME = 300;
export const VEHICLE_DETAILS = 'vehicleDetails';
export const VEHICLE_OWNER = 'vehicleOwner';
export const OTHER = 'other';
export const VEHICLE_NAME_KEY = 'name';
export const MYSELF_KEY = 'myself';
export const FAMILY_KEY = 'familyOrFriend';
export const BEREAVEMENT_KEY = 'probate';
export const COMPANY_KEY = 'company';
export const PRIVATE_PLATE = 'privatePlate';
export const KEEP_PRIVATE_PLATE = 'keepPrivatePlate';
export const ORIGINAL_VRM = 'originalVRM';
export const CHARGING_CABLES = 'chargingCables';
export const OWNED_BY_SELLER_KEY = 'v5LogBookOwnedBySeller';
export const HAS_SERVICE_RECORDS_KEY = 'hasServiceRecords';
export const KEEPER_TYPE = 'keeperType';
export const KEEPER_NAME = 'keeperName';
export const BATTERIES_IN_LEASE = 'areBatteriesInLease';
export const PRIVATE_PLATE_KEY = 'hasPrivatePlate';

export const SELLING_VRM_STATUS = 'sellingVrmStatus';

export const SELLER = 'seller';
export const OTHER_LIST = [FAMILY_KEY, BEREAVEMENT_KEY, COMPANY_KEY];
export const OWNER_LIST = [MYSELF_KEY, ...OTHER_LIST];

export const SELLING_VRM_STATUS_SELECTED = 'selected';
export const SELLING_VRM_STATUS_SKIPPED = 'skipped';
export const SELLING_VRM_STATUS_NOT_LISTED = 'not_listed';
export const SELLING_VRM_STATUSES = [
  SELLING_VRM_STATUS_SELECTED,
  SELLING_VRM_STATUS_SKIPPED,
  SELLING_VRM_STATUS_NOT_LISTED,
];

export const TOO_MANY_REQUESTS = 'too_many_requests';

export const defaultKeysToRemove = ['updatedAt', 'sellerLastActiveAt', 'vehicle.updatedAt', 'vehicleDetails.updatedAt', 'seller.updatedAt', 'vehicleDetails.plateHistory', 'plateHistory'];

export const NO_VALUE_TEXT = '–';
export const NONE_VALUE_TEXT = 'None';
export const SKIPPED_VALUE_TEXT = 'Skipped';

export const LOCATION_FIELDS = Object.freeze({
  address: ['address1', 'address2', 'address3'],
  city: 'city',
  postcode: 'postcode',
});

export const HAS_NEVER_BEEN_INAPPROPRIATELY_USED = 'hasNeverBeenInappropriatelyUsed';
export const HAS_NEVER_BEEN_WRITTEN_OFF_OR_STOLEN = 'hasNeverBeenWrittenOffOrStolen';
export const HAS_PROOF_OF_PURCHASE = 'hasProofOfPurchase';
export const HAS_V5_LOG_BOOK = 'hasV5LogBook';
export const HAS_SERVICE_RECORD = 'serviceRecord';

export const CHARGING_CABLES_KEYS = {
  EXIST_KEY: 'exist',
  NONE_KEY: 'none',
} as const;

export const CONFIRM_DETAILS_SECTIONS = {
  CONDITION: 'vehicleCondition',
  CONTACTS: 'contactDetails',
  INITIALISE: 'initialise',
  PHOTOS: 'vehiclePhotos',
  PRICE: 'price',
  PROFILE: 'profileDetails',
  SERVICE_HISTORY: 'serviceHistory',
  VEHICLE: 'vehicleDetails',
} as const;

export const UPDATED_VALUATION_REQUIREMENTS = {
  CONDITION: 'vehicleConditionConfirmedAt',
  CONTACTS: 'contactDetailsConfirmedAt',
  INITIALISE: 'initialisedAt',
  PHOTOS: 'vehiclePhotosConfirmedAt',
  SERVICE_HISTORY: 'serviceHistoryConfirmedAt',
  VEHICLE: 'vehicleDetailsConfirmedAt',
} as const;

export const ASSUMPTION_MODAL_VALUES = {
  [HAS_NEVER_BEEN_INAPPROPRIATELY_USED]: true,
  [HAS_NEVER_BEEN_WRITTEN_OFF_OR_STOLEN]: true,
} as Partial<VehicleDetails>;

export const SKIPPED_VALUE = 'skipped';

export const SKIPPED_HISTORY_MODAL_VALUES = {
  [HAS_SERVICE_RECORD]: SKIPPED_VALUE,
} as const;

export const IMAGE_STATUSES = {
  ARCHIVED: 'archived',
  DELETED: 'deleted',
  RESUPPLY: 'resupplyRequested',
} as const;

export const IMAGE_RETAKE_STATUSES:PhotoStatusType[] = [
  IMAGE_STATUSES.DELETED,
  IMAGE_STATUSES.RESUPPLY,
  IMAGE_STATUSES.ARCHIVED,
];

export const IMAGE_COUNT_MINIMUM = 15;

export const RESERVED_PRICE_STATUSES = [
  SLUGS.SOLD,
  SLUGS.SOLD_WAIT,
  SLUGS.SOLD_CONFIRMED,
  SLUGS.SOLD_DOCUMENTS_REVIEWED,
  SLUGS.SOLD_INVOICE_PAID,
  SLUGS.SOLD_INVOICE_RAISED,
  SLUGS.SOLD_PURCHASE_CONFIRMED,
  SLUGS.FOR_SALE,
  SLUGS.UNDER_OFFER,
  SLUGS.SALE_ACTIVE,
  SLUGS.READY_FOR_SALE,
] as const;

export const PROFILING_STATUSES = {
  CONFIRMING_RESERVE_WITH_SELLER: 'confirming_reserve_with_seller',
  FINANCE: 'finance',
  OTHER: 'other',
  PHOTOS_REQUESTED: 'photos_requested',
  POP_REQUESTED: 'pop_requested',
  PREP_QUERY: 'prep_query',
  SERVICE_HISTORY: 'service_history',
} as const;

export const TICKET_TIERS = {
  GOLDEN: 'gold',
  SILVER: 'silver',
} as const;

export const PREP_COST_STORAGE_KEY = 'prepCostSession-';

export const PREP_TYPES = {
  ELECTRIC_CHARGING_CABLES: 'electric_charging_cables',
  ELECTRIC_MISSING_BATTERIES: 'electric_missing_batteries',
  MISSING_KEYS: 'missing_keys',
  MISSING_LOCKING_WHEEL_NUT: 'missing_locking_wheel_nut',
  MISSING_TOOL_PACK: 'missing_tool_pack',
  NO_SERVICE_HISTORY: 'no_service_history',
  PANEL_DAMAGE_DENTS: 'panelDamageDents',
  PANEL_DAMAGE_PAINTWORK: 'panelDamagePaintwork',
  PANEL_DAMAGE_SCRATCHES: 'panelDamageScratches',
  SMOKED_IN: 'smoked_in',
  TYRE_DAMAGE_REPLACEMENT: 'tyreDamageReplacement',
  WHEEL_DAMAGE_ALLOYS: 'wheel_damage_alloys',
  WHEEL_DAMAGE_DIAMOND_CUT: 'wheel_damage_diamond_cut',
  WHEEL_DAMAGE_HUB_CAPS: 'wheel_damage_hub_caps',
} as const;

export const PREP_TYPE_FIELD_KEYS = {
  [PREP_TYPES.MISSING_KEYS]: 'keysCount',
  [PREP_TYPES.MISSING_LOCKING_WHEEL_NUT]: 'lockingWheelNut',
  [PREP_TYPES.SMOKED_IN]: 'hasBeenSmokedIn',
  [PREP_TYPES.MISSING_TOOL_PACK]: 'hasToolsInBoot',
  [PREP_TYPES.ELECTRIC_CHARGING_CABLES]: CHARGING_CABLES,
  [PREP_TYPES.ELECTRIC_MISSING_BATTERIES]: BATTERIES_IN_LEASE,
  [PREP_TYPES.WHEEL_DAMAGE_ALLOYS]: 'hasDamagedWheels',
  [PREP_TYPES.WHEEL_DAMAGE_DIAMOND_CUT]: 'hasDamagedWheels',
  [PREP_TYPES.WHEEL_DAMAGE_HUB_CAPS]: 'hasDamagedWheels',
  [PREP_TYPES.NO_SERVICE_HISTORY]: HAS_SERVICE_RECORD,
  [PREP_TYPES.PANEL_DAMAGE_DENTS]: 'hasDents',
  [PREP_TYPES.PANEL_DAMAGE_PAINTWORK]: 'hasPaintProblems',
  [PREP_TYPES.PANEL_DAMAGE_SCRATCHES]: 'hasScratches',
} as const;

export const FRIENDLY_COPY_PREP_TYPES: (
  typeof PREP_TYPES[keyof typeof PREP_TYPES]
)[] = [
  PREP_TYPES.TYRE_DAMAGE_REPLACEMENT,
  PREP_TYPES.PANEL_DAMAGE_PAINTWORK,
  PREP_TYPES.PANEL_DAMAGE_DENTS,
  PREP_TYPES.PANEL_DAMAGE_SCRATCHES,
  PREP_TYPES.WHEEL_DAMAGE_ALLOYS,
];

export const PREP_TYPE_NO_EDIT: (
  typeof PREP_TYPES[keyof typeof PREP_TYPES]
)[] = [
  PREP_TYPES.WHEEL_DAMAGE_ALLOYS,
  PREP_TYPES.WHEEL_DAMAGE_DIAMOND_CUT,
  PREP_TYPES.WHEEL_DAMAGE_HUB_CAPS,
  PREP_TYPES.TYRE_DAMAGE_REPLACEMENT,
  PREP_TYPES.PANEL_DAMAGE_PAINTWORK,
  PREP_TYPES.PANEL_DAMAGE_DENTS,
  PREP_TYPES.PANEL_DAMAGE_SCRATCHES,
];

export const RENAULT = 'renault';
export const NISSAN = 'nissan';
export const NISSAN_LEAF = 'leaf';
export const MYSELF_LOCAL_STORAGE_KEY = 'VehicleOtherMyself';

export const CONFIRM_PRICE_EXPIRED_ERROR_MSG = 'motorway.enquiries.confirm.enquiry_expired';

export const RETURNING_USER_LIMIT = 40;
export const DAYS_TO_EXPIRE_ENQUIRY = 7;
